import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardMedia,
  CardContent,
  Tabs,
  Tab,
  Dialog,
  DialogContent,
  IconButton,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import PhotoLibraryIcon from '@mui/icons-material/PhotoLibrary';
import VideoLibraryIcon from '@mui/icons-material/VideoLibrary';

const Gallery = () => {
  const [tabValue, setTabValue] = useState(0);
  const [selectedImage, setSelectedImage] = useState(null);

  // Placeholder data - replace with actual data from API
  const photoAlbums = [
    {
      id: 1,
      title: 'Education Programs',
      thumbnail: 'https://via.placeholder.com/400x300?text=Education+Programs',
      count: 25,
    },
    {
      id: 2,
      title: 'Women Empowerment Events',
      thumbnail: 'https://via.placeholder.com/400x300?text=Women+Empowerment',
      count: 18,
    },
    {
      id: 3,
      title: 'Cultural Festivals',
      thumbnail: 'https://via.placeholder.com/400x300?text=Cultural+Festivals',
      count: 32,
    },
    {
      id: 4,
      title: 'Youth Mentorship',
      thumbnail: 'https://via.placeholder.com/400x300?text=Youth+Mentorship',
      count: 15,
    },
    {
      id: 5,
      title: 'Community Events',
      thumbnail: 'https://via.placeholder.com/400x300?text=Community+Events',
      count: 28,
    },
    {
      id: 6,
      title: 'Workshops & Seminars',
      thumbnail: 'https://via.placeholder.com/400x300?text=Workshops',
      count: 22,
    },
  ];

  const videos = [
    {
      id: 1,
      title: 'GBCN Foundation - Our Mission',
      thumbnail: 'https://via.placeholder.com/400x300?text=Video+1',
      embedUrl: 'https://www.youtube.com/embed/dQw4w9WgXcQ',
    },
    {
      id: 2,
      title: 'Education Program Highlights',
      thumbnail: 'https://via.placeholder.com/400x300?text=Video+2',
      embedUrl: 'https://www.youtube.com/embed/dQw4w9WgXcQ',
    },
    {
      id: 3,
      title: 'Women Empowerment Initiative',
      thumbnail: 'https://via.placeholder.com/400x300?text=Video+3',
      embedUrl: 'https://www.youtube.com/embed/dQw4w9WgXcQ',
    },
  ];

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  return (
    <Box sx={{ minHeight: '80vh', py: 8, backgroundColor: '#ffffff' }}>
      <Container maxWidth="lg">
        <Typography
          variant="h2"
          component="h1"
          sx={{
            textAlign: 'center',
            fontWeight: 700,
            mb: 2,
            color: '#f97316',
          }}
        >
          Photo & Video Gallery
        </Typography>
        <Typography
          variant="h6"
          sx={{
            textAlign: 'center',
            color: 'text.secondary',
            mb: 6,
            maxWidth: '800px',
            mx: 'auto',
          }}
        >
          Explore our activities, events, and impact through photos and videos
        </Typography>

        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 4 }}>
          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            centered
            sx={{
              '& .MuiTab-root': {
                textTransform: 'none',
                fontSize: '1rem',
                fontWeight: 600,
              },
            }}
          >
            <Tab icon={<PhotoLibraryIcon />} iconPosition="start" label="Photo Gallery" />
            <Tab icon={<VideoLibraryIcon />} iconPosition="start" label="Video Gallery" />
          </Tabs>
        </Box>

        {tabValue === 0 && (
          <Grid container spacing={4}>
            {photoAlbums.map((album) => (
              <Grid item xs={12} sm={6} md={4} key={album.id}>
                <Card
                  sx={{
                    height: '100%',
                    cursor: 'pointer',
                    transition: 'transform 0.3s, box-shadow 0.3s',
                    '&:hover': {
                      transform: 'translateY(-8px)',
                      boxShadow: '0 8px 24px rgba(249, 115, 22, 0.15)',
                    },
                  }}
                  onClick={() => setSelectedImage(album.thumbnail)}
                >
                  <CardMedia
                    component="img"
                    height="240"
                    image={album.thumbnail}
                    alt={album.title}
                    sx={{ objectFit: 'cover' }}
                  />
                  <CardContent>
                    <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: '#111827' }}>
                      {album.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {album.count} photos
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}

        {tabValue === 1 && (
          <Grid container spacing={4}>
            {videos.map((video) => (
              <Grid item xs={12} md={6} key={video.id}>
                <Card
                  sx={{
                    height: '100%',
                    transition: 'transform 0.3s, box-shadow 0.3s',
                    '&:hover': {
                      transform: 'translateY(-8px)',
                      boxShadow: '0 8px 24px rgba(249, 115, 22, 0.15)',
                    },
                  }}
                >
                  <Box
                    sx={{
                      position: 'relative',
                      paddingTop: '56.25%', // 16:9 aspect ratio
                      backgroundColor: '#000',
                    }}
                  >
                    <iframe
                      src={video.embedUrl}
                      title={video.title}
                      style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        border: 'none',
                      }}
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                    />
                  </Box>
                  <CardContent>
                    <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: '#111827' }}>
                      {video.title}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}

        <Dialog
          open={!!selectedImage}
          onClose={() => setSelectedImage(null)}
          maxWidth="md"
          fullWidth
        >
          <DialogContent sx={{ p: 0, position: 'relative' }}>
            <IconButton
              onClick={() => setSelectedImage(null)}
              sx={{
                position: 'absolute',
                top: 8,
                right: 8,
                zIndex: 1,
                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                color: '#ffffff',
                '&:hover': {
                  backgroundColor: 'rgba(0, 0, 0, 0.7)',
                },
              }}
            >
              <CloseIcon />
            </IconButton>
            {selectedImage && (
              <Box
                component="img"
                src={selectedImage}
                alt="Gallery"
                sx={{ width: '100%', height: 'auto', display: 'block' }}
              />
            )}
          </DialogContent>
        </Dialog>
      </Container>
    </Box>
  );
};

export default Gallery;

